/**
 * BellCurveChart Component ("Where in the Bell")
 * Shows a rider's best timed run at one event plotted against the whole
 * field's distribution of best timed runs, for fun rather than rigorous
 * statistics — see memory/project_bell_curve_feature.md for the design
 * discussion behind the choices here (best time per rider, timed runs
 * only, all classes together by default with an exclude filter, no
 * pre-selected "frenemies" required — every rider is hoverable, frenemies
 * just get a permanent label on top of that).
 *
 * Rendered as two stacked charts sharing the same literal time domain
 * (not 'dataMin'/'dataMax') rather than one combined chart: mixing a
 * bin-indexed series (the bell shape) with point-based Scatter data in a
 * single ComposedChart made Recharts' shared tooltip index into each
 * series by array position rather than actual x-value, which showed the
 * wrong rider and mis-sized/invisible dots. Splitting them out gives the
 * scatter strip an accurate per-point hover, and the bell shape stays
 * purely decorative.
 */

import { useState, useMemo } from 'react';
import {
  ComposedChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const norm = (s) => (s || '').trim().toLowerCase();
const riderMatches = (entry, rider) =>
  rider && norm(entry.firstname) === norm(rider.firstname) && norm(entry.lastname) === norm(rider.lastname || '');
const riderName = (entry) => `${entry.firstname} ${entry.lastname || ''}`.trim();
const fmtTime = (t) => (t === null || t === undefined ? '—' : Number(t).toFixed(2));

const FRENEMY_COLOR = '#e74c3c';
const PRIMARY_COLOR = '#fb6107';
const OTHERS_COLOR = '#7f8c8d';
const AREA_COLOR = '#1b3d6d';

const CHART_MARGIN = { top: 10, right: 30, left: 20, bottom: 0 };

const ordinal = (n) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

const decileOf = (entry, sortedLength) => {
  const frac = entry.displayRank / sortedLength;
  const d = Math.min(10, Math.max(1, Math.ceil(frac * 10)));
  return `${ordinal(d)} decile`;
};

// Module-scope, not defined inside BellCurveChart, so it isn't torn down and
// recreated (losing Recharts' internal identity for it) on every render -
// `sorted` (needed for decileOf's percentile math) is passed through as a
// prop alongside Recharts' own injected `active`/`payload`.
const CustomTooltip = ({ active, payload, sorted }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    const pct = sorted.length <= 1 ? 100 : Math.round(((sorted.length - d.rank) / (sorted.length - 1)) * 100);
    const decile = decileOf({ displayRank: d.rank }, sorted.length);
    return (
      <div className="chart-tooltip">
        <p className="tooltip-date"><strong>{d.name}</strong></p>
        <p><strong>Time:</strong> {fmtTime(d.time)}s</p>
        <p><strong>Class:</strong> {d.class}</p>
        <p><strong>Position:</strong> {d.rank} of {sorted.length} ({pct}th percentile, {decile})</p>
      </div>
    );
  }
  return null;
};

function BellCurveChart({ results, rider, comparisonRiders = [], hill, date }) {
  const classResults = results?.class_results || {};
  const classNames = useMemo(() => Object.keys(classResults).sort(), [classResults]);
  const [excludedClasses, setExcludedClasses] = useState(new Set());

  const toggleClass = (className) => {
    setExcludedClasses(prev => {
      const next = new Set(prev);
      next.has(className) ? next.delete(className) : next.add(className);
      return next;
    });
  };

  const includedEntries = useMemo(() => {
    return classNames
      .filter(c => !excludedClasses.has(c))
      .flatMap(c => classResults[c] || [])
      .filter(r => typeof r.best_time === 'number');
  }, [classNames, excludedClasses, classResults]);

  const sorted = useMemo(() => {
    return [...includedEntries]
      .sort((a, b) => a.best_time - b.best_time)
      .map((r, i) => ({ ...r, displayRank: i + 1 }));
  }, [includedEntries]);

  const primaryEntry = useMemo(() => sorted.find(r => riderMatches(r, rider)), [sorted, rider]);
  const frenemyEntries = useMemo(
    () => comparisonRiders.map(fr => ({ rider: fr, entry: sorted.find(r => riderMatches(r, fr)) })),
    [sorted, comparisonRiders]
  );

  if (sorted.length === 0) {
    return (
      <div className="performance-chart">
        <div className="chart-header">
          <h2>🔔 Where in the Bell</h2>
          <p className="chart-info"><span className="hill-name">{hill} — {date}</span></p>
        </div>
        <p className="no-data">No timed runs match the current class filters.</p>
      </div>
    );
  }

  const times = sorted.map(r => r.best_time);
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);
  const range = Math.max(maxTime - minTime, 0.1);

  const binCount = Math.min(20, Math.max(8, Math.ceil(Math.sqrt(sorted.length))));
  const binWidth = range / binCount;
  const bins = Array.from({ length: binCount }, (_, i) => {
    const start = minTime + i * binWidth;
    return { x: start + binWidth / 2, count: 0 };
  });
  times.forEach(t => {
    let idx = Math.floor((t - minTime) / binWidth);
    if (idx >= binCount) idx = binCount - 1;
    if (idx < 0) idx = 0;
    bins[idx].count += 1;
  });
  // Zero-count phantom bins just outside the real range so the curve tapers
  // to zero on both edges instead of hard-cutting where the first/last real
  // bin happens to have a non-trivial count.
  bins.unshift({ x: bins[0].x - binWidth, count: 0 });
  bins.push({ x: bins[bins.length - 1].x + binWidth, count: 0 });
  // Domain matches the phantom bins exactly so the taper is fully visible
  // on both charts (they must share this literal range to stay aligned).
  const domainMin = bins[0].x;
  const domainMax = bins[bins.length - 1].x;

  const primaryName = primaryEntry ? riderName(primaryEntry) : null;
  const frenemyNames = new Set(frenemyEntries.filter(f => f.entry).map(f => riderName(f.entry)));

  const jitter = (i) => 0.12 + (i % 5) * 0.14; // deterministic spread, 0.12–0.68

  const toPoint = (r, i, role, y) => ({
    x: r.best_time,
    y,
    name: riderName(r),
    time: r.best_time,
    class: r.class,
    rank: r.displayRank,
    role,
  });

  // Others first (bottom of z-order), primary/frenemies appended last so
  // they render on top and aren't hidden behind overlapping grey dots.
  const dotPoints = [
    ...sorted
      .filter(r => riderName(r) !== primaryName && !frenemyNames.has(riderName(r)))
      .map((r, i) => toPoint(r, i, 'other', jitter(i))),
    ...frenemyEntries
      .filter(f => f.entry)
      .map((f, i) => toPoint(f.entry, i, 'frenemy', 0.85 + i * 0.001)),
    ...(primaryEntry ? [toPoint(primaryEntry, 0, 'primary', 0.5)] : []),
  ];

  const renderDot = (props) => {
    const { cx, cy, payload } = props;
    const radius = payload.role === 'primary' ? 7 : payload.role === 'frenemy' ? 6 : 3;
    const color = payload.role === 'primary' ? PRIMARY_COLOR : payload.role === 'frenemy' ? FRENEMY_COLOR : OTHERS_COLOR;
    const showLabel = payload.role !== 'other';
    return (
      <g>
        {/* Larger invisible circle so small/overlapping dots are still easy to hover accurately */}
        <circle cx={cx} cy={cy} r={Math.max(radius + 6, 10)} fill="transparent" pointerEvents="all" />
        <circle cx={cx} cy={cy} r={radius} fill={color} fillOpacity={payload.role === 'other' ? 0.6 : 1} pointerEvents="none" />
        {showLabel && (
          <text
            x={cx}
            y={cy - radius - 5}
            textAnchor="middle"
            fontSize={payload.role === 'primary' ? 12 : 11}
            fontWeight={payload.role === 'primary' ? 700 : 600}
            fill={color}
            pointerEvents="none"
          >
            {payload.name}
          </text>
        )}
      </g>
    );
  };

  /**
   * Percentile = % of the field this rider beat (faster than); quartile is
   * the coarser 4-bucket version of the same rank.
   */
  const percentileOf = (entry) => {
    if (sorted.length <= 1) return 100;
    return Math.round(((sorted.length - entry.displayRank) / (sorted.length - 1)) * 100);
  };
  const quartileOf = (entry) => {
    const frac = entry.displayRank / sorted.length;
    if (frac <= 0.25) return '1st quartile — fastest 25%';
    if (frac <= 0.5) return '2nd quartile';
    if (frac <= 0.75) return '3rd quartile';
    return '4th quartile — slowest 25%';
  };
  return (
    <div className="performance-chart">
      <div className="chart-header">
        <h2>🔔 Where in the Bell</h2>
        <p className="chart-info">
          <span className="rider-name">{rider.fullname}</span>
          <span className="hill-name">{hill} — {new Date(date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </p>
      </div>

      <div className="chart-controls">
        <p className="step-description">Exclude classes from the field:</p>
        <div className="checkbox-group">
          {classNames.map(c => (
            <label key={c} className="check-pill">
              <input
                type="checkbox"
                checked={!excludedClasses.has(c)}
                onChange={() => toggleClass(c)}
              />
              {excludedClasses.has(c) ? '' : '✓ '}{c} ({(classResults[c] || []).length})
            </label>
          ))}
        </div>
      </div>

      <div className="bell-summary">
        {primaryEntry ? (
          <p>
            <strong>{rider.fullname}</strong> — {fmtTime(primaryEntry.best_time)}s,
            position <strong>{primaryEntry.displayRank}</strong> of <strong>{sorted.length}</strong>
            {' '}— faster than <strong>{percentileOf(primaryEntry)}%</strong> of the field ({quartileOf(primaryEntry)}, {decileOf(primaryEntry, sorted.length)})
          </p>
        ) : (
          <p className="muted-note">No timed run recorded for {rider.fullname} at this event (or their class is currently excluded).</p>
        )}
        {frenemyEntries.map((f, i) => (
          <p key={i}>
            {f.entry ? (
              <>
                <strong style={{ color: FRENEMY_COLOR }}>{f.rider.fullname}</strong> — {fmtTime(f.entry.best_time)}s,
                position <strong>{f.entry.displayRank}</strong> of <strong>{sorted.length}</strong>
                {' '}— faster than <strong>{percentileOf(f.entry)}%</strong> of the field ({quartileOf(f.entry)}, {decileOf(f.entry, sorted.length)})
              </>
            ) : (
              <span className="muted-note">No timed run recorded for {f.rider.fullname} at this event (or their class is currently excluded).</span>
            )}
          </p>
        ))}
      </div>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={bins} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="x" type="number" domain={[domainMin, domainMax]} hide />
            <YAxis hide domain={[0, 'dataMax']} />
            <Area type="monotone" dataKey="count" stroke={AREA_COLOR} fill={AREA_COLOR} fillOpacity={0.25} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <ResponsiveContainer width="100%" height={180}>
          <ScatterChart margin={{ ...CHART_MARGIN, top: 30, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="x"
              type="number"
              domain={[domainMin, domainMax]}
              tickFormatter={(v) => `${v.toFixed(1)}s`}
              name="Time"
              unit="s"
            />
            <YAxis dataKey="y" type="number" domain={[0, 1]} hide />
            <Tooltip content={<CustomTooltip sorted={sorted} />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={dotPoints} shape={renderDot} isAnimationActive={false} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <p className="muted-note">
        Hover any dot to see who it is. {rider.fullname.split(' ')[0]}&apos;s run is highlighted in orange
        {comparisonRiders.length > 0 ? ', frenemies in red' : ''} — everyone else is shown in grey.
        Best timed run per rider only; practice runs aren&apos;t included.
      </p>
    </div>
  );
}

export default BellCurveChart;
